import { indexDataValue } from "../../../types/Codes";
import type { ElakeArvioModel } from "../../../types/ElakeArvioModel";
import type { CalculationContext } from "../calculationContext";
import {
  accumulatingPercentForYear,
  accrualMonthsForYear,
  type AccumulationSchedule,
  salaryMultipliersForYear,
} from "./rules";

export type AccumulationYearState = {
  readonly age: number;
  readonly income: number;
  readonly pension: number;
  readonly carryPension: number;
};

export type ApplyAccumulationYearInput = {
  readonly year: number;
  readonly startYear: number;
  readonly pensionYear: number;
  readonly state: AccumulationYearState;
  readonly model: Readonly<ElakeArvioModel>;
  readonly calculationMonth: number;
  readonly indexData?: CalculationContext["indexData"];
};

export type ApplyAccumulationYearResult = {
  readonly state: AccumulationYearState;
  readonly khi: number;
};

/** One calendar year of pension accumulation (age, income, pension, carry). */
export function applyAccumulationYear(
  input: ApplyAccumulationYearInput,
): ApplyAccumulationYearResult {
  const { year, startYear, pensionYear, state, model, calculationMonth, indexData } =
    input;
  const age = state.age + 1;

  const accumulatingPercent = accumulatingPercentForYear({
    year,
    age,
    birthMonth: model.birthMonth,
    pensionAgeYear: model.pensionAgeYear,
    pensionAgeMonth: model.pensionAgeMonth,
  });

  const {
    forIncome: salaryMultiplier,
    projectedPalkkakerroin: salayMultiplierFutureRaise,
  } = salaryMultipliersForYear({
    year,
    incomeDevelopment: model.incomeDevelopment,
    indexData,
  });
  const khi = indexDataValue(year, "KHI", indexData) || 1;

  let income = state.income;
  if (year === startYear) {
    income *= 1;
  } else {
    income *= salaryMultiplier;
  }

  const accrualMonths = accrualMonthsForYear({
    year,
    startYear,
    pensionYear,
    age,
    birthYear: model.birthYear,
    birthMonth: model.birthMonth,
    pensionAgeYear: model.pensionAgeYear,
    pensionAgeMonth: model.pensionAgeMonth,
    calculationMonth,
  });

  const newAccruall = income * accumulatingPercent * (accrualMonths / 12);

  let pension = state.pension;
  let carryPension = state.carryPension;
  if (year === startYear) {
    pension = pension * 1 + newAccruall;
    carryPension = carryPension * 1;
  } else if (year === startYear + 1) {
    pension = pension * salayMultiplierFutureRaise + newAccruall;
    carryPension = carryPension * salayMultiplierFutureRaise;
  } else {
    pension = pension * salaryMultiplier + newAccruall;
    carryPension = carryPension * salaryMultiplier;
  }

  return {
    state: { age, income, pension, carryPension },
    khi,
  };
}

export type YearlyAccumulationLoopOptions = {
  /** Track KHI/TEI price levels and exception salary index (main calculator path). */
  readonly trackPriceLevels: boolean;
  readonly onYearEnd?: (snapshot: AccumulationYearState) => void;
};

export type YearlyAccumulationLoopResult = {
  readonly state: AccumulationYearState;
  readonly exceptionSalaryIndex: number;
  readonly startPriceLevel: number;
  readonly endPriceLevel: number;
  readonly teiNextYear: number;
};

export function runYearlyAccumulationLoop(
  model: ElakeArvioModel,
  context: CalculationContext,
  schedule: AccumulationSchedule,
  options: YearlyAccumulationLoopOptions,
): YearlyAccumulationLoopResult {
  const { startYear, pensionYear, exceptionIndexing, ageBeforeStartYear } =
    schedule;

  let exceptionSalaryIndex = 1.0;
  let startPriceLevel = 1;
  let endPriceLevel = 1;
  let teiNextYear = 1;

  let state: AccumulationYearState = {
    age: ageBeforeStartYear,
    income: model.income,
    pension: model.accruedPension / context.lifetimeFactor,
    carryPension: model.accruedPension / context.lifetimeFactor,
  };

  for (let i = startYear; i <= pensionYear; ++i) {
    const { state: nextState, khi } = applyAccumulationYear({
      year: i,
      startYear,
      pensionYear,
      state,
      model,
      calculationMonth: context.calculationMonth,
      indexData: context.indexData,
    });
    state = nextState;

    if (options.trackPriceLevels) {
      teiNextYear = indexDataValue(i + 1, "TEI", context.indexData) || 1;
      if (i === startYear) {
        startPriceLevel = khi;
      }
      if (i === pensionYear && !exceptionIndexing) {
        endPriceLevel = khi;
      }
      if (exceptionIndexing) {
        endPriceLevel = indexDataValue(i + 1, "KHI", context.indexData) || 1;
        exceptionSalaryIndex =
          indexDataValue(i + 1, "Palkkakerroin", context.indexData) || 1;
      }
    } else if (exceptionIndexing) {
      exceptionSalaryIndex =
        indexDataValue(i + 1, "Palkkakerroin", context.indexData) || 1;
    }

    options.onYearEnd?.({ ...state });
  }

  if (exceptionIndexing) {
    state = {
      ...state,
      income: state.income * exceptionSalaryIndex,
      pension: state.pension * exceptionSalaryIndex,
      carryPension: state.carryPension * exceptionSalaryIndex,
    };
  }

  return {
    state,
    exceptionSalaryIndex,
    startPriceLevel,
    endPriceLevel,
    teiNextYear,
  };
}
